<template>
  <div class="topbar kt-topbar-toolbar-gap">
    <WarehouseSelect v-if="showWarehouseDropdown" />
    <UpgradeToPlus
      v-if="$store?.getters?.currentUser?.data?.subscription_enabled"
    />

    <!--begin: Language bar -->
    <div
      class="topbar-item language-bar"
      v-if="languageFlag && $store?.getters?.currentUser?.language_visibility"
    >
      <b-dropdown
        size="sm"
        variant="light"
        toggle-class="language-dropdown-toggle d-flex align-items-center text-decoration-none"
        menu-class="language-dropdown-menu-wrapper"
        right
        no-flip
        no-caret
        ref="langDropdown"
      >
        <template v-slot:button-content>
          <v-icon size="18" class="language-dropdown-toggle__globe">
            mdi-web
          </v-icon>
          <span class="language-dropdown-toggle__label">
            {{ languageFlag.name }}
          </span>
          <v-icon size="18" class="language-dropdown-toggle__chevron">
            mdi-chevron-down
          </v-icon>
        </template>
        <b-dropdown-text tag="div" class="language-dropdown-menu">
          <KTDropdownLanguage
            v-on:language-changed="onLanguageChanged"
          ></KTDropdownLanguage>
        </b-dropdown-text>
      </b-dropdown>
    </div>
    <!--end: Language bar -->

    <!--begin: User Bar -->
    <KTQuickUser v-if="getCurrentUser"></KTQuickUser>
    <!--end: User Bar -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import KTQuickUser from "@/view/layout/extras/offcanvas/QuickUser.vue";
import WarehouseSelect from "@/view/layout/extras/offcanvas/WarehouseSelect.vue";
import UpgradeToPlus from "@/view/layout/extras/offcanvas/UpgradeToPlus.vue";
import i18nService from "@/core/services/i18n.service.js";
import KTDropdownLanguage from "@/view/layout/extras/dropdown/DropdownLanguage.vue";

export default {
  name: "KTTopbar",
  data() {
    return {
      showWarehouseDropdown: false,
      languageFlag: null,
      languages: i18nService.languageslist,
    };
  },
  components: {
    KTQuickUser,
    WarehouseSelect,
    UpgradeToPlus,
    KTDropdownLanguage,
  },
  methods: {
    onLanguageChanged() {
      this.languageFlag = this.languages.find(
        (val) => val.abbreviation === i18nService.getActiveLanguage()
      );
      this.$refs["langDropdown"]?.hide();
    },
    checkRouteChange(route) {
      if (
        route === "/receiving" ||
        route === "/putaway_task" ||
        route === "/picking" ||
        route === "/packing" ||
        route === "/qc" ||
        route === "/packing" ||
        route === "/shipping"
      ) {
        this.showWarehouseDropdown = true;
        return;
      }
      this.showWarehouseDropdown = false;
    },
  },
  mounted() {
    this.onLanguageChanged();
  },
  computed: {
    getCurrentUser() {
      return this.$store.getters.currentUser.data;
    },
    ...mapGetters(["isClient", "isPlusUser"]),
  },
  watch: {
    $route(to) {
      this.checkRouteChange(to.path);
    },
  },
  beforeMount() {
    this.checkRouteChange(this.$route.path);
  },
};
</script>

<style lang="scss" scoped>
$lang-brand: #7e04b7;
$lang-text: #2b2c40;
$lang-muted: #6b7280;
$lang-border: #e6e8f1;
$lang-border-hover: #d6d9ea;
$lang-bg-hover: #faf6fe;

.kt-topbar-toolbar-gap {
  /* .topbar is already display:flex from layout SCSS; only add rhythm between tools */
  flex-wrap: wrap;
  align-items: center;
  column-gap: 0.5rem;
  row-gap: 0.35rem;
}

.language-bar {
  display: flex;
  align-items: center;
  // Keep small separation from the next item; main spacing is on the topbar
  margin-inline-end: 0;

  ::v-deep .language-dropdown-toggle {
    display: inline-flex !important;
    align-items: center;
    gap: 0.5rem;
    background-color: #ffffff !important;
    border: 1px solid $lang-border !important;
    border-radius: 999px !important;
    padding: 0.4rem 0.5rem 0.4rem 0.75rem !important;
    color: $lang-text !important;
    font-weight: 600;
    line-height: 1;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04) !important;
    transition: background-color 0.15s ease, border-color 0.15s ease,
      box-shadow 0.15s ease, transform 0.15s ease;

    &:hover {
      background-color: $lang-bg-hover !important;
      border-color: $lang-border-hover !important;
    }

    &:focus,
    &:focus-visible {
      outline: none !important;
      border-color: $lang-brand !important;
      box-shadow: 0 0 0 3px rgba(126, 4, 183, 0.15) !important;
    }

    &:active {
      transform: translateY(0.5px);
    }
  }

  ::v-deep .language-dropdown-toggle__globe {
    color: $lang-brand !important;
    flex-shrink: 0;
  }

  ::v-deep .language-dropdown-toggle__label {
    font-size: 0.875rem;
    letter-spacing: 0.01em;
    color: $lang-text;
  }

  ::v-deep .language-dropdown-toggle__chevron {
    color: $lang-muted !important;
    transition: transform 0.2s ease;
    flex-shrink: 0;
  }

  ::v-deep
    .show
    > .language-dropdown-toggle
    .language-dropdown-toggle__chevron {
    transform: rotate(180deg);
  }

  ::v-deep .dropdown-menu {
    min-width: 220px;
    margin-top: 0.5rem;
    padding: 0.5rem 0;
    border-radius: 0.75rem;
    border: 1px solid $lang-border;
    box-shadow: 0 12px 32px rgba(16, 24, 40, 0.12);
  }

  ::v-deep .language-dropdown-menu {
    padding: 0;
  }
}

/* Mirror icon order inside the pill for RTL (globe leads on the inline-start side). */
[dir="rtl"] .language-bar ::v-deep .language-dropdown-toggle {
  flex-direction: row-reverse;
}
</style>
